import { createTheme } from '@mui/material'

const theme = createTheme({
  palette: {
    primary: {
      main: '#6f88b3',
      light: '#a3bfe2',
      dark: '#004ba0'
    },
    secondary: {
      main: '#a4b1c2',
      light: '#ff5c8d',
      dark: '#9a0036'
    },
    success: {
      main: '#4caf50',
      light: '#80e27e',
      dark: '#087f23'
    },
    error: {
      main: '#f44336',
      light: '#ff7961',
      dark: '#ba000d'
    },
    warning: {
      main: '#ff9800',
      light: '#ffc947',
      dark: '#c66900'
    },
    info: {
      main: '#2196f3',
      light: '#6ec6ff',
      dark: '#0069c0'
    },
    text: {
      primary: '#212121',
      secondary: '#757575'
    },
    background: {
      default: '#fafafa',
      paper: '#ffffff'
    },
    divider: '#e0e0e0',

    // ── Custom tokens for uniform UI ──
    custom: {
      tableHeaderBg: '#f5f5f5',
      tableRowOddBg: '#f9f9f9',
      tableRowHoverBg: '#e3e9f2',
      tableRowSelectedBg: '#dce7f7',
      pageCardBg: '#ffffff',
      footerBg: '#f8f8f8',
      footerBorder: '#e7e7e7',
      dialogBg: '#f9f9fb'
    }
  },
  shape: {
    borderRadius: 8
  },
  typography: {
    fontFamily: 'Figtree, Roboto, sans-serif',
    h1: {
      fontSize: '2rem',
      fontWeight: 500
    },
    h2: {
      fontSize: '1.75rem',
      fontWeight: 500
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 500
    },
    h4: {
      fontSize: '1.25rem',
      fontWeight: 500
    },
    h5: {
      fontSize: '1.125rem',
      fontWeight: 500
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 500
    },
    body1: {
      fontSize: '1rem',
      fontWeight: 400
    },
    body2: {
      fontSize: '0.875rem',
      fontWeight: 400
    },
    caption: {
      fontSize: '0.75rem',
      fontWeight: 400
    }
  },

  // ── MUI Component Overrides for uniform look ──
  components: {
    // AppBar — consistent sticky, rounded page header
    MuiAppBar: {
      defaultProps: {
        position: 'sticky',
        color: 'primary'
      },
      styleOverrides: {
        root: {
          borderRadius: 4,
          marginBottom: 16
        }
      }
    },

    // Toolbar — centered content by default
    MuiToolbar: {
      styleOverrides: {
        root: {
          justifyContent: 'center'
        }
      }
    },

    // Button — no uppercase, consistent padding
    MuiButton: {
      defaultProps: {
        disableElevation: true
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 6,
          padding: '6px 20px'
        },
        containedPrimary: {
          '&:hover': {
            boxShadow: '0 2px 8px rgba(111, 136, 179, 0.4)'
          }
        }
      }
    },

    // TableCell — uniform header styling
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 600,
          backgroundColor: '#f5f5f5',
          borderBottom: '2px solid #e0e0e0',
          whiteSpace: 'nowrap'
        },
        root: {
          fontSize: '0.875rem',
          padding: '10px 16px'
        }
      }
    },

    // TableRow — uniform row striping & hover
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:nth-of-type(odd):not(.MuiTableRow-head)': {
            backgroundColor: '#f9f9f9'
          },
          '&:hover:not(.MuiTableRow-head)': {
            backgroundColor: '#e3e9f2'
          },
          '&.Mui-selected, &.Mui-selected:hover': {
            backgroundColor: '#dce7f7'
          },
          cursor: 'pointer'
        }
      }
    },

    // Dialog — consistent border-radius & background
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
        }
      }
    },

    // DialogTitle — primary colored title bar
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          backgroundColor: '#6f88b3',
          color: '#ffffff',
          fontWeight: 600,
          fontSize: '1.15rem',
          padding: '12px 24px',
          position: 'relative'
        }
      }
    },

    // TextField — consistent outlined variant
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'small'
      }
    },

    // Select — consistent size
    MuiSelect: {
      defaultProps: {
        size: 'small'
      }
    },

    // Autocomplete — consistent size
    MuiAutocomplete: {
      defaultProps: {
        size: 'small'
      }
    },

    // Paper — subtle shadow
    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: 8
        }
      }
    },

    // Scrollbar styling via CssBaseline
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarWidth: 'thin',
          scrollbarColor: '#888 #f1f1f1',
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px'
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1'
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '16px'
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#555'
          }
        },
        '*': {
          scrollbarWidth: 'thin',
          scrollbarColor: '#888 #f1f1f1'
        }
      }
    }
  }
})

export default theme
